<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { useConfirm } from '@/composables/useConfirm';

const { open: openConfirm } = useConfirm();

// Écran unique des accès premium : chercher un porteur (club, comité, ligue ou
// événement), lui accorder un accès pour une durée, le révoquer. Miroir de
// Dartshub/backend/modules/api/admin/premium.py -- remplace les bascules dispersées
// (AdminClubs/Comites/Ligues, fiche événement) qui ne posaient aucune durée.

type TargetType = 'club' | 'comite' | 'ligue' | 'event';
type Duration = 'season' | 'event' | 'custom' | 'unlimited';

interface PremiumTarget {
  target_type: TargetType;
  target_id: number;
  name: string;
  // Nom affiché : numéroté pour les opens officiels ("Open de Comité #2"), = name sinon.
  display_name?: string | null;
  subtitle: string | null;
  enabled: boolean;
  until: string | null;
  active: boolean;
  event_date: string | null;
  event_type: string | null;
  owner_type: 'club' | 'comite' | 'ligue' | null;
  owner_id: number | null;
  owner_label: string | null;
  event_until_default: string | null;
  // Événements : niveau de l'officiel et, s'il est déjà débloqué sans accès unitaire,
  // par quoi (abonnement du gestionnaire ou pack comité/ligue).
  level: 'comite' | 'ligue' | 'federal' | null;
  covered_by: string | null;
}

const TYPE_LABEL: Record<TargetType, string> = { club: 'Club', comite: 'Comité', ligue: 'Ligue', event: 'Événement' };
const TYPE_BADGE: Record<TargetType, string> = { club: 'badge-blue', comite: 'badge-purple', ligue: 'badge-indigo', event: 'badge-teal' };
// Grille tarifaire indicative (cf. page /tarifs de Compet) : abonnement club = ses
// locaux, pack comité/ligue = ses officiels de ce niveau quel que soit le club hôte,
// accès à l'unité selon le niveau de l'événement.
const PRICE_HINT: Record<TargetType, string> = {
  club: '45 € / saison (ses tournois locaux)',
  comite: '100 € / saison (pack : ses officiels de niveau comité, quel que soit le club hôte)',
  ligue: '250 € / saison (pack : ses officiels de niveau ligue)',
  event: "à l'unité : 15 € local · 30 € comité · 60 € ligue ou national",
};
const LEVEL_LABEL: Record<string, string> = { comite: 'niveau comité', ligue: 'niveau ligue', federal: 'niveau fédéral' };

const key = (t: { target_type: TargetType; target_id: number }) => `${t.target_type}-${t.target_id}`;
const label = (t: { name: string; display_name?: string | null }) => t.display_name || t.name;
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : 'sans limite');

// ── Accès en cours ────────────────────────────────────────────────────────
const loading = ref(false);
const access = ref<PremiumTarget[]>([]);
const seasonLabel = ref('');
const seasonEnd = ref<string | null>(null);
const listFilter = ref<'all' | TargetType>('all');

const filteredAccess = computed(() =>
  listFilter.value === 'all' ? access.value : access.value.filter(a => a.target_type === listFilter.value)
);

const daysLeft = (until: string | null) => {
  if (!until) return null;
  const ms = new Date(until).getTime() - new Date(new Date().toDateString()).getTime();
  return Math.round(ms / 86400000);
};

const statusOf = (a: PremiumTarget): { label: string; cls: string } => {
  if (!a.enabled && a.covered_by) return { label: `Couvert : ${a.covered_by}`, cls: 'badge-teal' };
  if (!a.enabled) return { label: 'Standard', cls: 'badge-gray' };
  if (!a.active) return { label: 'Expiré', cls: 'badge-red' };
  const d = daysLeft(a.until);
  if (d !== null && d <= 30) return { label: `Expire dans ${d} j`, cls: 'badge-orange' };
  return { label: 'Actif', cls: 'badge-green' };
};

const loadAccess = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/admin/premium/access');
    access.value = data.items;
    seasonLabel.value = data.season_label;
    seasonEnd.value = data.season_end;
  } catch {
    toast.error('Erreur de chargement des accès.');
  } finally {
    loading.value = false;
  }
};

// ── Recherche d'un porteur ────────────────────────────────────────────────
const query = ref('');
const searching = ref(false);
const results = ref<PremiumTarget[]>([]);
let searchTimer: ReturnType<typeof setTimeout> | null = null;

const runSearch = async () => {
  const q = query.value.trim();
  if (q.length < 2) { results.value = []; return; }
  searching.value = true;
  try {
    const { data } = await dartsHubApi.get('/admin/premium/search', { params: { q } });
    results.value = data;
  } catch {
    toast.error('Erreur de recherche.');
  } finally {
    searching.value = false;
  }
};

watch(query, () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 250);
});

// ── Accorder / prolonger ──────────────────────────────────────────────────
const selected = ref<PremiumTarget | null>(null);
const duration = ref<Duration>('season');
const customUntil = ref('');
const granting = ref(false);

const select = (t: PremiumTarget) => {
  selected.value = t;
  duration.value = t.target_type === 'event' ? 'event' : 'season';
  customUntil.value = t.until ?? '';
};

const clearSelection = () => { selected.value = null; };

const plannedUntil = computed(() => {
  if (!selected.value) return null;
  if (duration.value === 'season') return seasonEnd.value;
  if (duration.value === 'event') return selected.value.event_until_default;
  if (duration.value === 'custom') return customUntil.value || null;
  return null;
});

const canGrant = computed(() =>
  !!selected.value && !granting.value && (duration.value !== 'custom' || !!customUntil.value)
);

const grant = async () => {
  if (!selected.value) return;
  granting.value = true;
  try {
    const { data } = await dartsHubApi.post('/admin/premium/grant', {
      target_type: selected.value.target_type,
      target_id: selected.value.target_id,
      duration: duration.value,
      until: duration.value === 'custom' ? customUntil.value : null,
    });
    toast.success(`Accès accordé à ${label(data)} jusqu'au ${fmtDate(data.until)}.`);
    selected.value = null;
    query.value = '';
    results.value = [];
    await loadAccess();
  } catch (err: any) {
    toast.error(err?.response?.data?.detail || "Erreur lors de l'activation.");
  } finally {
    granting.value = false;
  }
};

const revoking = ref<string | null>(null);
const revoke = async (t: PremiumTarget) => {
  if (!(await openConfirm(
    "Révoquer l'accès",
    `Retirer l'accès premium de ${TYPE_LABEL[t.target_type].toLowerCase()} "${label(t)}" ? ` +
    (t.target_type === 'event' ? "Cet événement ne sera plus gérable." : 'Tous ses événements ne seront plus gérables.')
  ))) return;
  revoking.value = key(t);
  try {
    await dartsHubApi.delete(`/admin/premium/${t.target_type}/${t.target_id}`);
    toast.success('Accès révoqué.');
    await loadAccess();
  } catch (err: any) {
    toast.error(err?.response?.data?.detail || 'Erreur lors de la révocation.');
  } finally {
    revoking.value = null;
  }
};

// ── Gestionnaire d'un événement (qui gère et encaisse) ────────────────────
interface EntityOption { id: number; label: string }
const entityOptions = ref<Record<'club' | 'comite' | 'ligue', EntityOption[]>>({ club: [], comite: [], ligue: [] });
const entitiesLoaded = ref(false);

const loadEntities = async () => {
  if (entitiesLoaded.value) return;
  const [clubs, comites, ligues] = await Promise.all([
    dartsHubApi.get('/clubs/', { params: { all_comites: true } }),
    dartsHubApi.get('/clubs/comites'),
    dartsHubApi.get('/clubs/ligues'),
  ]);
  entityOptions.value = {
    club: clubs.data.map((c: any) => ({ id: c.id, label: c.name })),
    comite: comites.data.map((c: any) => ({ id: c.id, label: `${c.name} (${c.code})` })),
    ligue: ligues.data.map((l: any) => ({ id: l.id, label: `${l.name} (${l.code})` })),
  };
  entitiesLoaded.value = true;
};

const editingEvent = ref<PremiumTarget | null>(null);
const editOwnerType = ref<'club' | 'comite' | 'ligue'>('club');
const editOwnerId = ref<number | null>(null);
const savingOwner = ref(false);

const openOwnerEditor = async (t: PremiumTarget) => {
  try { await loadEntities(); } catch { toast.error('Erreur de chargement des entités.'); return; }
  editingEvent.value = t;
  editOwnerType.value = t.owner_type ?? 'club';
  editOwnerId.value = t.owner_id;
};

const saveOwner = async () => {
  if (!editingEvent.value || !editOwnerId.value) return;
  savingOwner.value = true;
  try {
    await dartsHubApi.patch(`/events/${editingEvent.value.target_id}/premium-owner`, null, {
      params: { owner_type: editOwnerType.value, owner_id: editOwnerId.value },
    });
    toast.success('Gestionnaire mis à jour.');
    editingEvent.value = null;
    await Promise.all([loadAccess(), runSearch()]);
    if (selected.value?.target_type === 'event') {
      const fresh = [...access.value, ...results.value].find(r => key(r) === key(selected.value!));
      if (fresh) selected.value = fresh;
    }
  } catch (err: any) {
    toast.error(err?.response?.data?.detail || 'Erreur lors de la mise à jour du gestionnaire.');
  } finally {
    savingOwner.value = false;
  }
};

// ── Événements de démonstration ───────────────────────────────────────────
// event_type="demo" : même formulaire et mêmes règles que le vrai événement, sans
// HelloAsso, invisible du calendrier public, premium à la création (cf.
// events/routes.py::create_demo_event).
const showDemo = ref(false);
const demoEvents = ref<any[]>([]);
const creatingDemo = ref(false);
const demoForm = ref({
  name: 'Coupe de la Ligue - DÉMO',
  event_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  ligue: 'SDE',
  comite: '',
  format: 'simple+double',
  premium_owner_type: 'ligue' as 'club' | 'comite' | 'ligue',
  premium_owner_id: null as number | null,
  price_euros: 10,
});

const loadDemoEvents = async () => {
  const { data } = await dartsHubApi.get('/events/');
  demoEvents.value = data.filter((e: any) => e.event_type === 'demo');
};

const toggleDemo = async () => {
  showDemo.value = !showDemo.value;
  if (showDemo.value) {
    try { await Promise.all([loadEntities(), loadDemoEvents()]); } catch { toast.error('Erreur de chargement.'); }
  }
};

const createDemo = async () => {
  const f = demoForm.value;
  if (!f.name.trim() || !f.premium_owner_id) return;
  creatingDemo.value = true;
  try {
    const { data } = await dartsHubApi.post('/events/demo', {
      name: f.name, event_date: f.event_date, ligue: f.ligue || null, comite: f.comite || null,
      format: f.format, premium_owner_type: f.premium_owner_type, premium_owner_id: f.premium_owner_id,
      price_cents: Math.round(f.price_euros * 100),
    });
    toast.success(`Événement de démo créé : ${demoLink(data.id)}`);
    await Promise.all([loadDemoEvents(), loadAccess()]);
  } catch (err: any) {
    toast.error(err?.response?.data?.detail || "Erreur lors de la création de l'événement de démo.");
  } finally {
    creatingDemo.value = false;
  }
};

const demoLink = (id: number) => `https://compet.dartshub.fr/evenement/${id}`;
const copiedDemoId = ref<number | null>(null);
const copyDemoLink = async (id: number) => {
  try {
    await navigator.clipboard.writeText(demoLink(id));
    copiedDemoId.value = id;
    setTimeout(() => { if (copiedDemoId.value === id) copiedDemoId.value = null; }, 2000);
  } catch {
    toast.info(demoLink(id));
  }
};

const resettingDemo = ref<number | null>(null);
const resetDemo = async (e: any) => {
  if (!(await openConfirm('Réinitialiser la démo', `Supprimer toutes les inscriptions de "${e.name}" ?`))) return;
  resettingDemo.value = e.id;
  try {
    await dartsHubApi.delete(`/inscriptions/event/${e.id}`);
    toast.success('Inscriptions de démo supprimées.');
  } catch {
    toast.error('Erreur lors de la réinitialisation.');
  } finally {
    resettingDemo.value = null;
  }
};

onMounted(loadAccess);
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-crown" /> Accès premium</h1>
      <span v-if="seasonLabel" class="badge badge-gray">Saison {{ seasonLabel }} · fin le {{ fmtDate(seasonEnd) }}</span>
    </div>

    <p class="intro">
      Un accès = un porteur + une date de fin. <strong>Club</strong> : abonnement à la saison pour les événements qu'il
      gère (ses locaux). <strong>Comité / ligue</strong> : pack à la saison qui couvre tous ses officiels de ce niveau,
      quel que soit le club hôte qui gère et encaisse (jamais vers le bas : le pack ligue ne couvre pas les opens de
      comité). <strong>Événement</strong> : accès à l'unité sur cet événement seulement, jusqu'à sa fin + 30 jours.
      Passé la date de fin, l'accès s'affiche "expiré" et se prolonge d'un clic.
    </p>

    <!-- ── Donner un accès ─────────────────────────────────────────────── -->
    <section class="card grant-card">
      <h2><i class="fa-solid fa-key" /> Donner ou prolonger un accès</h2>

      <div v-if="!selected" class="search-bar">
        <i class="fa-solid" :class="searching ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'" style="color:var(--text-muted)" />
        <input v-model="query" placeholder="Nom d'un club, comité, ligue ou événement…" autofocus />
      </div>

      <ul v-if="!selected && results.length" class="results">
        <li v-for="r in results" class="result" @click="select(r)" :key="key(r)">
          <span class="badge" :class="TYPE_BADGE[r.target_type]">{{ TYPE_LABEL[r.target_type] }}</span>
          <div class="result-main">
            <div class="result-name">{{ label(r) }}</div>
            <div class="result-sub">
              {{ r.subtitle }}<template v-if="r.level"> · {{ LEVEL_LABEL[r.level] }}</template><template v-if="r.owner_label"> · gestionnaire {{ r.owner_label }}</template>
            </div>
          </div>
          <span class="badge" :class="statusOf(r).cls">
            {{ statusOf(r).label }}<template v-if="r.enabled && r.until"> · {{ fmtDate(r.until) }}</template>
          </span>
        </li>
      </ul>
      <p v-else-if="!selected && query.trim().length >= 2 && !searching" class="muted">Aucun résultat.</p>

      <div v-if="selected" class="grant-form">
        <div class="selected-row">
          <span class="badge" :class="TYPE_BADGE[selected.target_type]">{{ TYPE_LABEL[selected.target_type] }}</span>
          <div class="result-main">
            <div class="result-name">{{ label(selected) }}</div>
            <div class="result-sub">
              {{ selected.subtitle }}
              <template v-if="selected.target_type === 'event'">
                <template v-if="selected.level"> · {{ LEVEL_LABEL[selected.level] }}</template>
                · gestionnaire {{ selected.owner_label || 'aucun' }}
                <button class="btn btn-ghost btn-sm" title="Changer le gestionnaire" @click="openOwnerEditor(selected)">
                  <i class="fa-solid fa-pen" />
                </button>
              </template>
            </div>
            <div v-if="selected.enabled" class="result-sub">
              Accès actuel : {{ statusOf(selected).label }} · fin {{ fmtDate(selected.until) }}
            </div>
            <div v-else-if="selected.covered_by" class="result-sub covered">
              Déjà couvert par {{ selected.covered_by }} : un accès à l'unité est inutile tant que ce pack court.
            </div>
          </div>
          <button class="btn btn-ghost btn-sm" title="Changer de porteur" @click="clearSelection">
            <i class="fa-solid fa-xmark" /> Changer
          </button>
        </div>

        <div class="duration-grid">
          <label v-if="selected.target_type === 'event'" class="duration" :class="{ active: duration === 'event' }">
            <input v-model="duration" type="radio" value="event" />
            <span class="duration-title">Cet événement</span>
            <span class="duration-sub">Jusqu'au {{ fmtDate(selected.event_until_default) }} (fin + 30 jours)</span>
          </label>
          <label class="duration" :class="{ active: duration === 'season' }">
            <input v-model="duration" type="radio" value="season" />
            <span class="duration-title">Saison {{ seasonLabel }}</span>
            <span class="duration-sub">Jusqu'au {{ fmtDate(seasonEnd) }}</span>
          </label>
          <label class="duration" :class="{ active: duration === 'custom' }">
            <input v-model="duration" type="radio" value="custom" />
            <span class="duration-title">Date personnalisée</span>
            <input v-model="customUntil" type="date" class="date-input" :disabled="duration !== 'custom'" @click.stop />
          </label>
          <label class="duration" :class="{ active: duration === 'unlimited' }">
            <input v-model="duration" type="radio" value="unlimited" />
            <span class="duration-title">Sans limite</span>
            <span class="duration-sub">Pilotes, partenaires, usage interne</span>
          </label>
        </div>

        <div class="grant-actions">
          <span class="muted">
            Tarif indicatif : {{ PRICE_HINT[selected.target_type] }}<template v-if="plannedUntil"> · accès jusqu'au {{ fmtDate(plannedUntil) }}</template>
          </span>
          <button class="btn btn-primary" :disabled="!canGrant" @click="grant">
            <i class="fa-solid fa-check" /> {{ granting ? 'Activation…' : (selected.enabled ? "Prolonger l'accès" : 'Activer') }}
          </button>
        </div>
      </div>
    </section>

    <!-- ── Accès en cours ──────────────────────────────────────────────── -->
    <div class="list-header">
      <h2>Accès accordés <span class="badge badge-gray">{{ filteredAccess.length }}</span></h2>
      <select v-model="listFilter" class="filter-select">
        <option value="all">Tous</option>
        <option value="ligue">Ligues</option>
        <option value="comite">Comités</option>
        <option value="club">Clubs</option>
        <option value="event">Événements</option>
      </select>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Porteur</th>
            <th>Gestionnaire</th>
            <th>Fin d'accès</th>
            <th>Statut</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in filteredAccess" :class="{ expired: !a.active }" :key="key(a)">
            <td>
              <div class="result-main">
                <div class="result-name">
                  <span class="badge" :class="TYPE_BADGE[a.target_type]">{{ TYPE_LABEL[a.target_type] }}</span>
                  {{ label(a) }}
                </div>
                <div class="result-sub">{{ a.subtitle }}</div>
              </div>
            </td>
            <td>
              <template v-if="a.target_type === 'event'">
                {{ a.owner_label || 'aucun' }}
                <button class="btn btn-ghost btn-sm" title="Changer le gestionnaire" @click="openOwnerEditor(a)">
                  <i class="fa-solid fa-pen" />
                </button>
              </template>
              <span v-else class="muted">lui-même</span>
            </td>
            <td>{{ fmtDate(a.until) }}</td>
            <td><span class="badge" :class="statusOf(a).cls">{{ statusOf(a).label }}</span></td>
            <td class="actions">
              <button class="btn btn-ghost btn-sm" title="Prolonger / modifier la durée" @click="select(a)">
                <i class="fa-solid fa-calendar-plus" /> Prolonger
              </button>
              <button class="btn btn-ghost btn-sm btn-revoke" title="Révoquer l'accès" :disabled="revoking === key(a)" @click="revoke(a)">
                <i class="fa-solid fa-ban" />
              </button>
            </td>
          </tr>
          <tr v-if="filteredAccess.length === 0">
            <td colspan="5" style="text-align:center;color:var(--text-muted)">Aucun accès accordé</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Démonstration ───────────────────────────────────────────────── -->
    <section class="demo-block">
      <button class="btn btn-ghost btn-sm" @click="toggleDemo">
        <i class="fa-solid fa-flask" /> {{ showDemo ? 'Masquer les événements de démonstration' : 'Événements de démonstration' }}
      </button>
      <div v-if="showDemo" class="demo-form">
        <p class="muted" style="margin:0">
          Pour faire tester le formulaire d'inscription (mêmes règles que le vrai événement, ex. coupe réservée aux
          licenciés de la ligue), sans aucun paiement, invisible du calendrier public. Le nom doit commencer comme le
          vrai type (« Coupe de la Ligue », « Coupe du Comité »...) pour en reprendre les règles.
        </p>
        <div class="demo-grid">
          <label>Nom <input v-model="demoForm.name" class="filter-select" /></label>
          <label>Date <input v-model="demoForm.event_date" type="date" class="filter-select" /></label>
          <label>Ligue (code) <input v-model="demoForm.ligue" class="filter-select" placeholder="SDE" /></label>
          <label>Comité (code) <input v-model="demoForm.comite" class="filter-select" placeholder="MER" /></label>
          <label>Format
            <select v-model="demoForm.format" class="filter-select">
              <option value="simple">Simple</option>
              <option value="double">Double</option>
              <option value="simple+double">Simple + double</option>
            </select>
          </label>
          <label>Tarif / catégorie (€) <input v-model.number="demoForm.price_euros" type="number" min="1" class="filter-select" /></label>
          <label>Gestionnaire
            <select v-model="demoForm.premium_owner_type" class="filter-select" @change="demoForm.premium_owner_id = null">
              <option value="club">Club</option>
              <option value="comite">Comité</option>
              <option value="ligue">Ligue</option>
            </select>
          </label>
          <label>&nbsp;
            <select v-model="demoForm.premium_owner_id" class="filter-select">
              <option :value="null" disabled>-- Choisir --</option>
              <option v-for="opt in entityOptions[demoForm.premium_owner_type]" :value="opt.id" :key="opt.id">{{ opt.label }}</option>
            </select>
          </label>
        </div>
        <div>
          <button class="btn btn-primary" :disabled="creatingDemo || !demoForm.premium_owner_id || !demoForm.name.trim()" @click="createDemo">
            {{ creatingDemo ? 'Création...' : 'Créer' }}
          </button>
        </div>
        <ul v-if="demoEvents.length" class="results">
          <li v-for="e in demoEvents" class="result result-static" :key="e.id">
            <div class="result-main">
              <div class="result-name">{{ e.name }} <span class="muted">· {{ e.event_date }}</span></div>
              <a :href="demoLink(e.id)" target="_blank" rel="noopener" class="result-sub">{{ demoLink(e.id) }}</a>
            </div>
            <button class="btn btn-ghost btn-sm" title="Copier le lien" @click="copyDemoLink(e.id)">
              <i class="fa-solid" :class="copiedDemoId === e.id ? 'fa-check' : 'fa-copy'" />
            </button>
            <button class="btn btn-ghost btn-sm" title="Supprimer toutes les inscriptions" :disabled="resettingDemo === e.id" @click="resetDemo(e)">
              <i class="fa-solid fa-rotate-left" />
            </button>
          </li>
        </ul>
      </div>
    </section>

    <!-- ── Modale gestionnaire ─────────────────────────────────────────── -->
    <div v-if="editingEvent" class="modal-overlay" @click.self="editingEvent = null">
      <div class="modal">
        <h2>Gestionnaire de "{{ label(editingEvent) }}"</h2>
        <p class="muted">Qui gère les inscriptions de cet événement et avec quels identifiants HelloAsso il encaisse.
          C'est aussi son abonnement (s'il en a un) qui débloque l'événement.</p>
        <div class="form-group">
          <label>Type de gestionnaire</label>
          <select v-model="editOwnerType" class="filter-select" style="width:100%" @change="editOwnerId = null">
            <option value="club">Club</option>
            <option value="comite">Comité</option>
            <option value="ligue">Ligue</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ TYPE_LABEL[editOwnerType] }}</label>
          <select v-model="editOwnerId" class="filter-select" style="width:100%">
            <option :value="null" disabled>-- Choisir --</option>
            <option v-for="opt in entityOptions[editOwnerType]" :value="opt.id" :key="opt.id">{{ opt.label }}</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost" @click="editingEvent = null">Annuler</button>
          <button class="btn btn-primary" :disabled="savingOwner || !editOwnerId" @click="saveOwner">
            {{ savingOwner ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.intro {
  color: var(--text-muted);
  font-size: 0.88rem;
  max-width: 80ch;
  margin: 0 0 1rem;
}

.grant-card {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.grant-card h2,
.list-header h2 {
  font-size: 1.05rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.results {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 360px;
  overflow-y: auto;
}

.result {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
}

.result:hover {
  background: color-mix(in srgb, var(--primary) 6%, transparent);
}

.result-static {
  cursor: default;
}

.result-static:hover {
  background: transparent;
}

.result-main {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.result-sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  word-break: break-word;
}

.muted {
  color: var(--text-muted);
  font-size: 0.85rem;
}

.covered {
  color: var(--warning);
  font-weight: 600;
}

.grant-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.selected-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--primary);
  border-radius: var(--radius);
  background: color-mix(in srgb, var(--primary) 6%, transparent);
}

.duration-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.6rem;
}

.duration {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
}

.duration.active {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.duration input[type="radio"] {
  display: none;
}

.duration-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.duration-sub {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.date-input,
.filter-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  outline: none;
}

.grant-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

tr.expired td {
  opacity: 0.7;
}

.actions {
  white-space: nowrap;
}

.btn-revoke:hover {
  color: var(--danger);
}

.demo-block {
  margin-top: 1.5rem;
}

.demo-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px dashed var(--border);
  border-radius: var(--radius);
}

.demo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

.demo-grid label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
  text-align: left;
}

.modal .form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.4rem;
}
</style>
